/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable camelcase */
import { handle } from 'redux-pack';
import {
	ADD_SHOT,
	EDIT_SHOT,
	LOAD_SHOTS,
	SET_PLAYING_SHOT,
	BULK_EDIT_SHOTS,
	UPDATE_SHOT_ORDER,
	SET_SHOT_MODAL_SHOT,
	DELETE_SHOT,
	SET_SHOT_ID_TO_DELETE,
	SYNC_SHOTS_REDUCER_STATE,
	SET_CURRENT_SHOT_ID,
	SET_VIDEO_TITLES_HAVE_FIRED,
	UPDATE_SHOT_DATA,
	UPDATE_CANVAS_STYLE,
	REMOVE_SHOT,
	SET_DELETED_SHOT_ORDER,
	DUPLICATE_SHOT,
	SET_SHOT_TAB_SHOT_ID,
	SET_SHOT_MODAL_LOCKED,
	LOAD_SHOT,
	DISABLE_SHOT_AUTOPLAY,
} from '../actions/shots';
import Socket from '../../connection/socket';
import type { AppSettings } from '../types/settings';
import { baseSettings } from './settings';
import { EntityType } from '../../types/enums';
import { Entity } from '../../types/interfaces';
import { decodeHtmlEntities, isSafeUrl } from '../../utils/utils';
import { SHOT_THUMBNAIL_TYPES } from '../types/shots';
import type {
	ShotFeed,
	ShotData,
	Shot,
	ShotsState,
} from '../types/shots';

export const FEED_TYPES = {
	MEDIA_AUDIO: 'audio',
	MEDIA: 'media',
	PRESENTER: 'presenter',
	EMPTY: 'empty',
	EMAIL: 'email',
	SCREENSHARE: 'screenshare',
	SIP_CALL: 'sipCall',
	SRT_STREAM: 'srtStream',
	ONSITE_LOCATION: 'onsiteLocation',
} as const;

export const PLAY_STATES = {
	PLAYING: 0,
	ENDING: 1,
	LATE: 2,
};

interface Action {
	type: string;
	payload: any;
	remote?: boolean;
}

export const baseShotFeed: ShotFeed = {
	media: null,
	presenter: null,
	email: null,
	screenshare: null,
	sipStreamId: null,
	srtStreamId: null,
	onsiteLocationId: null,
	feedType: FEED_TYPES.EMPTY,
	position: 0,
};

export const baseShotData: ShotData = {
	forcedLayout: 'single cover',
	shotTitle: '',
	videoTitlesOn: false,
	shotFeeds: [baseShotFeed],
	shotDuration: 0,
	order: 0,
	cue: '',
	note: '',
	autoplay: false,
	autoDuration: false,
};

export const baseShot: Shot = {
	shot_id: 0,
	created_by: 0,
	edited_by: 0,
	meeting: 0,
	shot_style: {
		applied: baseSettings,
		queue: baseSettings,
	},
	shot_data: baseShotData,
	edited: ''
};

export const initialShotsState: ShotsState = {
	shots: [baseShot],
	addingShot: false,
	editingShot: false,
	loadingShots: false,
	shotsLoaded: false,
	addingShotError: '',
	editingShotError: '',
	loadingShotsError: '',
	shotStartTimestampEntity: null,
	shotModalShot: baseShot,
	shotIdToDelete: null,
	previousShotId: 0,
	currentShotId: 0,
	videoTitlesHaveFired: false,
	canvasStyle: baseSettings,
	deletedShotOrder: null,
	currentShotIdEntity: null,
	shotTabShotId: 0,
	shotModalEditTimestamp: 0,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let socket: Socket | null = null;

const updateShotDataIfMatch = (
	shot: Shot,
	idToUpdate: number,
	newShotData: ShotData,
	thumbnailUrl?: string,
	edited?: string,
): Shot => {
	const returnedShot = shot;
	if (idToUpdate === returnedShot.shot_id) {
		returnedShot.shot_data = newShotData;
		if (edited) {
			returnedShot.thumbnail_url = (thumbnailUrl && isSafeUrl(thumbnailUrl)) ? thumbnailUrl : undefined;
			returnedShot.edited = new Date().toISOString();
		}
	}
	return returnedShot;
};

const addAndReOrderShots = (currentShots: Shot[], addedShot: Shot): Shot[] => {
	const {
		shot_id: shotId,
		shot_data: { order },
	} = addedShot;

	const newShots = currentShots
		.filter((s) => s.shot_id !== shotId)
		.map((s) => ({
			...s,
			shot_data: {
				...s.shot_data,
				order: s.shot_data.order >= order
					? s.shot_data.order + 1
					: s.shot_data.order,
			},
		}));

	newShots.splice(order, 0, addedShot);
	return newShots;
};

const deleteAndReOrderShots = (currentShots: Shot[], delShotId: number): Shot[] => {
	let orderOfDeleted = 9999;

	return currentShots
		.filter((s) => {
			if (s.shot_id === delShotId) {
				orderOfDeleted = s.shot_data.order;
				return false;
			}
			return true;
		})
		.map((s) => ({
			...s,
			shot_data: {
				...s.shot_data,
				order: s.shot_data.order > orderOfDeleted
					? s.shot_data.order - 1
					: s.shot_data.order,
			},
		}));
};

const actions: Map<any, Function> = new Map([
	[
		ADD_SHOT,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			shots: addAndReOrderShots(state.shots, action.payload),
		}),
	],
	[
		EDIT_SHOT,
		(state: ShotsState, action: Action) => handle(state, action, {
			start: (state) => ({ ...state, editingShot: true }),
			finish: (state) => ({ ...state, editingShot: false }),
			failure: (state) => ({
				...state,
				editingShotError: 'Error editing shot. Please try again.',
			}),
			success: (state) => ({ ...state }),
		}),
	],
	[
		BULK_EDIT_SHOTS,
		(state: ShotsState, action: Action) => handle(state, action, {
			start: (state) => ({ ...state, editingShot: true }),
			finish: (state) => ({ ...state, editingShot: false }),
			failure: (state) => ({
				...state,
				editingShotError: 'Error editing shot. Please try again.',
			}),
			success: (state) => ({ ...state }),
		}),
	],
	[
		LOAD_SHOTS,
		(state: ShotsState, action: Action) => handle(state, action, {
			start: (state) => ({ ...state, loadingShots: true }),
			finish: (state) => ({ ...state, loadingShots: false }),
			failure: (state) => ({
				...state,
				loadingShotsError: 'Error loading shots. Please try again.',
				shotsLoaded: false,
			}),
			success: (state) => {
				const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
				return {
					...state,
					shots: JSON.parse(sanitizedPayload),
					shotsLoaded: true,
					canvasStyle: action?.payload[0]?.shot_style?.applied ?? baseSettings,
				};
			},
		}),
	],
	[
		LOAD_SHOT,
		(state: ShotsState, action: Action) => handle(state, action, {
			start: (state) => ({ ...state, loadingShots: true }),
			finish: (state) => ({ ...state, loadingShots: false }),
			failure: (state) => ({
				...state,
				loadingShotsError: 'Error loading shot. Please try again.',
				shotsLoaded: false,
			}),
			success: (state) => {
				const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
				const updatedShot: Shot = JSON.parse(sanitizedPayload);
				return {
					...state,
					shots: state.shots.map(
						(s: Shot) => updateShotDataIfMatch(s, updatedShot.shot_id, updatedShot.shot_data),
					),
				};
			},
		}),
	],
	[
		DELETE_SHOT,
		(state: ShotsState, action: Action) => handle(state, action, {
			start: (state) => ({ ...state }),
			finish: (state) => ({ ...state }),
			failure: (state) => ({
				...state,
				loadingShotsError: 'Error deleting shot.',
			}),
			success: (state) => ({ ...state }),
		}),
	],
	[
		SET_SHOT_ID_TO_DELETE,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			shotIdToDelete: action.payload,
		}),
	],
	[
		SET_SHOT_MODAL_SHOT,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			shotModalShot: action.payload,
		}),
	],
	[EntityType.shotStartTimestamp, (state: ShotsState, action: Action): ShotsState => ({
		...state,
		shotStartTimestampEntity: {
			...action.payload,
		} as Entity,
	})],
	[
		SET_PLAYING_SHOT,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			shotStartTimestampEntity: {
				...action.payload,
			} as Entity,
		}),
	],
	[
		UPDATE_SHOT_ORDER,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			shots: action.payload,
		}),
	],
	[
		SYNC_SHOTS_REDUCER_STATE,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			...action.payload,
		}),
	],
	[
		SET_CURRENT_SHOT_ID,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			currentShotId: action.payload,
		}),
	],
	[
		SET_VIDEO_TITLES_HAVE_FIRED,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			videoTitlesHaveFired: action.payload,
		}),
	],
	[
		UPDATE_SHOT_DATA,
		(state: ShotsState, { payload: p }: Action): ShotsState => {
			const sanitizedPayload = decodeHtmlEntities(JSON.stringify(p));
			const parsedPayload = JSON.parse(sanitizedPayload);
			return {
				...state,
				shots: state.shots.map(
					(s: Shot) => updateShotDataIfMatch(
						s,
						parsedPayload.shotId,
						parsedPayload.shotData,
						parsedPayload.thumbnailUrl,
						parsedPayload.edited,
					),
				),
			};
		},
	],
	[
		DISABLE_SHOT_AUTOPLAY,
		(state: ShotsState, action: Action): ShotsState => {
			const shotId = action.payload;
			return {
				...state,
				shots: state.shots.map((shot) =>
					shot.shot_id === shotId
						? {
							...shot,
							shot_data: {
								...shot.shot_data,
								autoplay: false,
							},
						  }
						: shot
				),
			};
		},
	],
	[
		UPDATE_CANVAS_STYLE,
		(state: ShotsState, { payload: p }: Action): ShotsState => ({
			...state,
			canvasStyle: p,
		}),
	],
	[
		REMOVE_SHOT,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			shots: deleteAndReOrderShots(state.shots, action.payload),
		}),
	],
	[
		SET_DELETED_SHOT_ORDER,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			deletedShotOrder: action.payload,
		}),
	],
	[EntityType.currentShotId, (state, action): ShotsState => {
		const {
			currentShotId,
			shotTabShotId,
		} = state;
		const updatedShotId: number = action.payload?.entityValue?.currentShotId
			?? state.currentShotId;
		const shouldUpdateShotTab: boolean = shotTabShotId === currentShotId;
		return {
			...state,
			currentShotIdEntity: action.payload,
			previousShotId: currentShotId,
			currentShotId: updatedShotId,
			...(shouldUpdateShotTab && {
				shotTabShotId: updatedShotId,
			}),
		};
	}],
	[
		DUPLICATE_SHOT,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			shots: addAndReOrderShots(state.shots, action.payload),
		}),
	],
	[
		SET_SHOT_TAB_SHOT_ID,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			shotTabShotId: action.payload,
		}),
	],
	[
		SET_SHOT_MODAL_LOCKED,
		(state: ShotsState, action: Action): ShotsState => ({
			...state,
			shotModalEditTimestamp: action.payload,
		}),
	],
]);

// eslint-disable-next-line default-param-last
export const ShotsReducer = (state: ShotsState = initialShotsState, action: Action): ShotsState => {
	const newState = actions.get(action.type)?.(state, action) || state;
	return newState;
};

export function setShotReducerSocket(sock: Socket): void {
	socket = sock;
}
